import { useEffect, useRef, useState } from 'react'
import { clsx } from 'clsx'

interface Line {
  text: string
  at: string
}

/**
 * Consola en vivo por WebSocket. El backend valida permisos antes de abrir el
 * stream; si no los hay, cierra con 4403 y acá se muestra el motivo.
 */
export function LiveConsole({ deploymentId }: { deploymentId: string }) {
  const [lines, setLines] = useState<Line[]>([])
  const [state, setState] = useState<'connecting' | 'open' | 'closed' | 'denied'>('connecting')
  const [message, setMessage] = useState<string | null>(null)
  const [follow, setFollow] = useState(true)
  const [filter, setFilter] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const ws = new WebSocket(`${proto}://${location.host}/ws/deployments/${deploymentId}/logs?lines=300`)

    ws.onopen = () => setState('open')
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as { type: string; data?: string; at?: string }
        if (msg.type === 'line' && msg.data !== undefined) {
          setLines((prev) => {
            const next = [...prev, { text: msg.data!, at: msg.at ?? new Date().toISOString() }]
            // Techo de memoria: la UI no guarda un historial infinito.
            return next.length > 3000 ? next.slice(-2000) : next
          })
        } else if (msg.type === 'error') {
          setMessage(msg.data ?? 'Error en el stream')
        } else if (msg.type === 'ready') {
          setMessage(null)
        }
      } catch {
        /* mensaje no-JSON: se ignora */
      }
    }
    ws.onclose = (ev) => setState(ev.code === 4403 ? 'denied' : 'closed')
    ws.onerror = () => setState('closed')

    return () => ws.close()
  }, [deploymentId])

  useEffect(() => {
    if (follow && boxRef.current) {
      boxRef.current.scrollTop = boxRef.current.scrollHeight
    }
  }, [lines, follow])

  const visible = filter ? lines.filter((l) => l.text.toLowerCase().includes(filter.toLowerCase())) : lines

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="input w-56"
          placeholder="Filtrar líneas…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-slate-400">
          <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
          Seguir el final
        </label>
        <button className="btn-ghost text-xs" onClick={() => setLines([])}>Limpiar</button>
        <span className={clsx('ml-auto text-xs',
          state === 'open' ? 'text-green-400' : state === 'denied' ? 'text-red-400' : 'text-slate-500')}>
          {state === 'open' ? '● en vivo' : state === 'connecting' ? 'conectando…' : state === 'denied' ? 'sin permiso' : 'desconectado'}
        </span>
      </div>

      {message && <p className="text-sm text-red-400">{message}</p>}

      <div
        ref={boxRef}
        onScroll={(e) => {
          const el = e.currentTarget
          const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40
          if (!atBottom && follow) setFollow(false)
        }}
        className="h-[28rem] overflow-auto rounded-lg border bg-black/40 p-3 font-mono text-xs leading-relaxed"
      >
        {visible.length === 0 ? (
          <p className="text-slate-600">
            {state === 'denied' ? 'No tenés permiso para ver la consola de esta instancia.' : 'Sin líneas todavía…'}
          </p>
        ) : (
          visible.map((l, i) => (
            <div key={i} className="whitespace-pre-wrap break-all text-slate-300">
              {l.text}
            </div>
          ))
        )}
      </div>
      <p className="hint">{visible.length} línea(s){filter && ` · filtradas de ${lines.length}`}</p>
    </div>
  )
}
